import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOnboarding } from '../../context/OnboardingContext';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import './onboarding.css';

interface OnboardingLayoutProps {
  children: React.ReactNode;
  title: string;
  subtitle: string;
  showCancelButton?: boolean;
  showBackButton?: boolean;
  onBack?: () => void;
}

const OnboardingLayout: React.FC<OnboardingLayoutProps> = ({ children, title, subtitle, showCancelButton = true, showBackButton = false, onBack }) => {
  const { currentStep, totalSteps } = useOnboarding();
  const { theme, toggleTheme } = useTheme();

  return (
    <div className="min-h-screen bg-xervix-bg-primary dark:bg-xervix-bg-primary-dark flex items-center justify-center p-4">
      <div className="bg-xervix-bg-secondary dark:bg-xervix-bg-secondary-dark rounded-lg shadow-lg w-full max-w-4xl md:p-8 p-2 relative md:h-[580px] h-[100%] flex flex-col ">
        {/* Botón de volver atrás */}
        {showBackButton && onBack && (
          <button 
            onClick={onBack}
            className="absolute top-4 left-4 p-2 rounded-md bg-xervix-bg-tertiary dark:bg-xervix-bg-tertiary-dark hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors duration-300"
            aria-label="Volver atrás"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-xervix-text-primary dark:text-xervix-text-primary-dark" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}

        {/* Encabezado */}
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-xervix-primary mb-2">XERVIX</h1>
          <h2 className="md:text-2xl text-xl font-semibold text-xervix-text-primary dark:text-xervix-text-primary-dark mb-2">
            {title}
          </h2>
          <p className="text-xervix-text-secondary dark:text-xervix-text-secondary-dark text-sm">
            {subtitle}
          </p>
        </div>

        {/* Contenido principal */}
        <div className="flex-grow mb-6">
          {children}
        </div>

        {/* Indicador de progreso - siempre al fondo */}
        <div className="flex justify-center items-center mb-4 mt-auto">
          <div className="flex space-x-2">
            {Array.from({ length: totalSteps }).map((_, index) => (
              <div
                key={index}
                className={`h-2 w-8 rounded-full ${
                  index + 1 <= currentStep
                    ? 'bg-xervix-primary'
                    : 'bg-xervix-bg-tertiary dark:bg-xervix-bg-tertiary-dark'
                }`}
              ></div>
            ))}
          </div>
        </div>

        {/* Botón de cambio de tema */}
        <button 
            onClick={toggleTheme} 
            className="absolute top-4 right-4 p-2 rounded-md bg-xervix-bg-tertiary hover:bg-gray-300 dark:bg-xervix-bg-tertiary-dark dark:hover:bg-gray-600 transition-colors duration-300"
            aria-label={`Cambiar a modo ${theme === 'dark' ? 'claro' : 'oscuro'}`}
          >
            {theme === 'dark' ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-yellow-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-800" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
              </svg>
            )}
          </button>
      </div>
    </div>
  );
};

// Componente de botón de cancelar que puede ser usado en las páginas
export const CancelButton: React.FC<{ onClick?: () => void }> = ({ onClick }) => {
  const { resetOnboarding } = useOnboarding();
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const [showCancelModal, setShowCancelModal] = useState(false);

  const handleCancelClick = () => {
    if (onClick) {
      onClick();
    } else {
      setShowCancelModal(true);
    }
  };

  const handleConfirmCancel = async () => {
    try {
      // 1. Limpiar datos del onboarding
      resetOnboarding();
      
      // 2. Cerrar sesión (especialmente importante si es usuario anónimo)
      await signOut();
      
      // 3. Cerrar modal
      setShowCancelModal(false);
      
      // 4. Navegar al landing page
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Error al cancelar el onboarding:', error);
      // Aún así intentar navegar al landing
      setShowCancelModal(false);
      navigate('/', { replace: true });
    }
  };

  const handleCloseCancelModal = () => {
    setShowCancelModal(false);
  };

  return (
    <>
      <button
        onClick={handleCancelClick}
        className="px-10 py-3 rounded-md bg-transparent border-2 border-xervix-tertiary hover:border-xervix-primary text-xervix-text-secondary dark:text-xervix-text-secondary-dark hover:text-xervix-primary dark:hover:text-xervix-primary font-medium transition-colors"
      >
        Cancelar
      </button>

      {/* Modal de confirmación */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-xervix-bg-secondary dark:bg-xervix-bg-secondary-dark rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-center mb-4">
              <div className="bg-red-100 dark:bg-red-900 rounded-full p-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
            </div>
            <h3 className="text-xl font-bold text-xervix-text-primary dark:text-xervix-text-primary-dark text-center mb-2">
              ¿Cancelar registro?
            </h3>
            <p className="text-xervix-text-secondary dark:text-xervix-text-secondary-dark text-center mb-6">
              Si cancelas ahora, perderás todo el progreso de tu registro. Esta acción no se puede deshacer.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleCloseCancelModal}
                className="flex-1 px-4 py-2 rounded-md bg-xervix-bg-tertiary dark:bg-xervix-bg-tertiary-dark text-xervix-text-primary dark:text-xervix-text-primary-dark hover:bg-opacity-80 transition-colors font-medium"
              >
                Continuar registro
              </button>
              <button
                onClick={handleConfirmCancel}
                className="flex-1 px-4 py-2 rounded-md bg-xervix-primary text-white hover:bg-red-600 transition-colors font-medium"
              >
                Sí, cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OnboardingLayout;
